import { useState, useEffect, useRef, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  startEmailCode,
  verifyEmailCode,
  mergeEmailIdentity,
  updateDisplayName,
  setTokens,
  getMe,
  type EmailAuthSession,
} from '../lib/api';
import { AuthLayout } from '../components/AuthLayout';
import { MicrosoftLogo } from '../components/MicrosoftLogo';
import { generateCodeVerifier, computeCodeChallenge, generateState } from '../lib/pkce';
import {
  getMicrosoftClientId,
  getMicrosoftRedirectUri,
  buildMicrosoftAuthorizeUrl,
  MICROSOFT_PKCE_VERIFIER_KEY,
  MICROSOFT_PKCE_STATE_KEY,
} from '../lib/microsoftAuth';

type Step =
  | { name: 'email' }
  | { name: 'code' }
  | { name: 'merge'; provider: 'apple' | 'microsoft'; mergeToken: string }
  | { name: 'name'; session: EmailAuthSession };

const errorTextStyle: React.CSSProperties = {
  color: 'var(--color-danger)',
  fontSize: '0.875rem',
  marginBottom: 'var(--space-4)',
};

export function SignInPage() {
  const { isAuthenticated, isLoading, onTokensReceived } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<Step>({ name: 'email' });
  const microsoftClientId = getMicrosoftClientId();
  const ranPendingSession = useRef(false);

  /** Persists a verified session's tokens, checks for a missing display name
   * (R-358 rule 7 / R-32), and either prompts for one or finalizes sign-in. */
  async function proceedWithSession(session: EmailAuthSession) {
    setTokens(session.accessToken, session.refreshToken);
    localStorage.setItem('anchor_access', session.accessToken);
    localStorage.setItem('anchor_refresh', session.refreshToken);

    const me = await getMe();
    if (!me.displayName.trim()) {
      setStep({ name: 'name', session });
      return;
    }
    await onTokensReceived(session.accessToken, session.refreshToken);
  }

  // Promotes a session handed off by MicrosoftCallbackPage (R-306) after a
  // successful Microsoft sign-in. Runs once on mount; clears the router state
  // immediately so a refresh of /sign-in never replays it.
  useEffect(() => {
    // Guard against React StrictMode's double-invoke in dev: both invocations
    // would otherwise read location.state before either's navigate() takes
    // effect, and could both call proceedWithSession. Same pattern as
    // MicrosoftCallbackPage's `ran` ref.
    if (ranPendingSession.current) return;
    const pendingSession = (location.state as { pendingSession?: EmailAuthSession } | null)
      ?.pendingSession;
    if (!pendingSession) return;
    ranPendingSession.current = true;
    navigate(location.pathname, { replace: true, state: null });
    proceedWithSession(pendingSession).catch(() => {
      setError('Something went wrong. Please try again.');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isLoading && isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  function resetToEmail() {
    setStep({ name: 'email' });
    setCode('');
    setError(null);
  }

  async function handleStartCode(e: FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await startEmailCode(email.trim());
      setStep({ name: 'code' });
    } catch {
      setError('Something went wrong. Check your email address and try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleVerifyCode(e: FormEvent) {
    e.preventDefault();
    if (!code.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await verifyEmailCode(email.trim(), code.trim());
      switch (result.kind) {
        case 'success':
          await proceedWithSession(result);
          break;
        case 'existing_account':
          setStep({ name: 'merge', provider: result.provider, mergeToken: result.mergeToken });
          break;
        case 'error':
          setError(result.message);
          break;
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleMerge() {
    if (step.name !== 'merge') return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await mergeEmailIdentity(step.mergeToken);
      if (result.kind === 'success') {
        await proceedWithSession(result);
      } else {
        setError(result.message);
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleNameSubmit(e: FormEvent) {
    e.preventDefault();
    if (step.name !== 'name' || !name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await updateDisplayName(name.trim());
      await onTokensReceived(step.session.accessToken, step.session.refreshToken);
    } catch {
      setError('That name doesn’t look right. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  /** Kicks off the R-306 browser Authorization Code + PKCE flow: generates a
   * verifier/challenge/state, stashes the verifier and state in sessionStorage
   * (short-lived, single-use, this-tab-only — unlike the long-lived session
   * tokens in localStorage), and does a full-page redirect to Microsoft. The
   * code_verifier never appears in this URL or any URL — only in the POST
   * body MicrosoftCallbackPage later sends to /v1/auth/microsoft. */
  async function handleMicrosoftSignIn() {
    if (!microsoftClientId) return;
    setError(null);
    const codeVerifier = generateCodeVerifier();
    const state = generateState();
    const codeChallenge = await computeCodeChallenge(codeVerifier);
    sessionStorage.setItem(MICROSOFT_PKCE_VERIFIER_KEY, codeVerifier);
    sessionStorage.setItem(MICROSOFT_PKCE_STATE_KEY, state);
    const authorizeUrl = buildMicrosoftAuthorizeUrl({
      clientId: microsoftClientId,
      redirectUri: getMicrosoftRedirectUri(),
      codeChallenge,
      state,
    });
    window.location.href = authorizeUrl;
  }

  return (
    <AuthLayout
      footer={
        <p
          style={{
            marginTop: 'var(--space-6)',
            textAlign: 'center',
            fontSize: '0.8125rem',
            color: 'var(--color-text-subtle)',
          }}
        >
          Need an account?{' '}
          <a href="https://howisyourenginerunning.com" style={{ color: 'var(--color-accent)' }}>
            Learn about Anchor
          </a>
        </p>
      }
    >
      {step.name === 'email' && (
            <>
              <h2 style={{ marginBottom: 'var(--space-5)' }}>Sign in</h2>

              {microsoftClientId && (
                <>
                  <button
                    type="button"
                    className="btn btn--secondary"
                    style={{ width: '100%', marginBottom: 'var(--space-5)' }}
                    onClick={handleMicrosoftSignIn}
                    aria-label="Sign in with Microsoft"
                  >
                    <MicrosoftLogo />
                    Sign in with Microsoft
                  </button>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-3)',
                      marginBottom: 'var(--space-5)',
                      color: 'var(--color-text-subtle)',
                      fontSize: '0.8125rem',
                    }}
                  >
                    <span style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
                    or
                    <span style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
                  </div>
                </>
              )}

              <form onSubmit={handleStartCode} noValidate>
                <div className="form-group" style={{ marginBottom: 'var(--space-4)' }}>
                  <label className="form-label" htmlFor="email">
                    Work email
                  </label>
                  <input
                    id="email"
                    type="email"
                    className="form-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@yourorg.com"
                    autoComplete="email"
                    required
                    autoFocus
                  />
                </div>

                {error && (
                  <p role="alert" style={errorTextStyle}>
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  className="btn btn--primary"
                  style={{ width: '100%' }}
                  disabled={submitting || !email.trim()}
                >
                  {submitting ? 'Sending…' : 'Send code'}
                </button>
              </form>

              <p
                style={{
                  marginTop: 'var(--space-5)',
                  fontSize: '0.8125rem',
                  color: 'var(--color-text-subtle)',
                  textAlign: 'center',
                }}
              >
                For Apple Sign In, open the Anchor iOS app and sign in there first.
                <br />
                Enterprise SSO coming in v1.1.
              </p>
            </>
          )}

          {step.name === 'code' && (
            <>
              <h2 style={{ marginBottom: 'var(--space-3)' }}>Check your email</h2>
              <p style={{ color: 'var(--color-text-muted)', fontSize: '0.9375rem', marginBottom: 'var(--space-5)' }}>
                We sent a 6-digit code to <strong>{email}</strong>. It expires in 15 minutes.
              </p>

              <form onSubmit={handleVerifyCode} noValidate>
                <div className="form-group" style={{ marginBottom: 'var(--space-4)' }}>
                  <label className="form-label" htmlFor="code">
                    Verification code
                  </label>
                  <input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    className="form-input"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    autoComplete="one-time-code"
                    required
                    autoFocus
                  />
                </div>

                {error && (
                  <p role="alert" style={errorTextStyle}>
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  className="btn btn--primary"
                  style={{ width: '100%' }}
                  disabled={submitting || code.trim().length !== 6}
                >
                  {submitting ? 'Verifying…' : 'Verify'}
                </button>
              </form>

              <button
                className="btn btn--ghost btn--sm"
                style={{ marginTop: 'var(--space-6)' }}
                onClick={resetToEmail}
              >
                Use a different email
              </button>
            </>
          )}

          {step.name === 'merge' && (
            <div style={{ textAlign: 'center', padding: 'var(--space-4) 0' }}>
              <h2 style={{ marginBottom: 'var(--space-3)' }}>Account already exists</h2>
              <p style={{ color: 'var(--color-text-muted)', fontSize: '0.9375rem', marginBottom: 'var(--space-5)' }}>
                You already signed up with{' '}
                {step.provider === 'apple' ? 'Apple' : 'Microsoft'}. Use email to sign in to
                that same account?
              </p>

              {error && (
                <p role="alert" style={errorTextStyle}>
                  {error}
                </p>
              )}

              <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'center' }}>
                <button className="btn btn--ghost" onClick={resetToEmail} disabled={submitting}>
                  Not now
                </button>
                <button className="btn btn--primary" onClick={handleMerge} disabled={submitting}>
                  {submitting ? 'Merging…' : 'Merge'}
                </button>
              </div>
            </div>
          )}

          {step.name === 'name' && (
            <>
              <h2 style={{ marginBottom: 'var(--space-3)' }}>What should we call you?</h2>
              <p style={{ color: 'var(--color-text-muted)', fontSize: '0.9375rem', marginBottom: 'var(--space-5)' }}>
                Your teammates will see this name.
              </p>

              <form onSubmit={handleNameSubmit} noValidate>
                <div className="form-group" style={{ marginBottom: 'var(--space-4)' }}>
                  <label className="form-label" htmlFor="displayName">
                    Display name
                  </label>
                  <input
                    id="displayName"
                    type="text"
                    className="form-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jordan Lee"
                    maxLength={80}
                    autoComplete="name"
                    required
                    autoFocus
                  />
                </div>

                {error && (
                  <p role="alert" style={errorTextStyle}>
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  className="btn btn--primary"
                  style={{ width: '100%' }}
                  disabled={submitting || !name.trim()}
                >
                  {submitting ? 'Saving…' : 'Continue'}
                </button>
              </form>
            </>
          )}
    </AuthLayout>
  );
}
